import { LOCAL_STORAGE_KEYS, WORKSPACE_ENVIRONMENT } from '@/app.constants';

type EnvironmentMap = Record<string, string | undefined>;
type CustomEnvOverride = {
  key?: string;
  value?: string;
};

export const ENVIRONMENT_KEYS = {
  VITE_PUSHER_APP_KEY: 'VITE_PUSHER_APP_KEY',
  VITE_PUSHER_APP_CLUSTER: 'VITE_PUSHER_APP_CLUSTER',
  VITE_PUSHER_CHANNEL_NAME_PREFIX: 'VITE_PUSHER_CHANNEL_NAME_PREFIX'
} as const;

export type EnvironmentKey = typeof ENVIRONMENT_KEYS[keyof typeof ENVIRONMENT_KEYS];

export const OVERRIDABLE_ENV_KEYS = [
  ENVIRONMENT_KEYS.VITE_PUSHER_APP_CLUSTER,
  ENVIRONMENT_KEYS.VITE_PUSHER_CHANNEL_NAME_PREFIX,
  ENVIRONMENT_KEYS.VITE_PUSHER_APP_KEY,
] as const;

const OVERRIDABLE_ENV_KEYS_SET = new Set<string>(OVERRIDABLE_ENV_KEYS);

const getCustomEnvironmentOverrides = (): EnvironmentMap => {
  if (import.meta.env.VITE_ENVIRONMENT !== WORKSPACE_ENVIRONMENT.DEVELOPMENT) {
    return {};
  }

  if (typeof window === 'undefined') {
    return {};
  }

  const rawOverrides = window.localStorage.getItem(LOCAL_STORAGE_KEYS.DEVELOPER_ENV_OVERRIDES);
  if (!rawOverrides) {
    return {};
  }

  try {
    const parsedOverrides = JSON.parse(rawOverrides) as CustomEnvOverride[];
    return parsedOverrides.reduce<EnvironmentMap>((acc, currentOverride) => {
      if (
        currentOverride?.key &&
        OVERRIDABLE_ENV_KEYS_SET.has(currentOverride.key)
      ) {
        acc[currentOverride.key] = currentOverride.value;
      }
      return acc;
    }, {});
  } catch (error) {
    console.error('Failed to parse custom env overrides:', error);
    return {};
  }
};

export const getEnvironmentValue = (environmentKey: EnvironmentKey, fallbackValue?: string): string | undefined => {
  const customEnvironmentOverrides = getCustomEnvironmentOverrides();
  if (Object.prototype.hasOwnProperty.call(customEnvironmentOverrides, environmentKey)) {
    return customEnvironmentOverrides[environmentKey];
  }

  const value = (import.meta.env as EnvironmentMap)[environmentKey];
  return value ?? fallbackValue;
};
